import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { rupiah } from "./perjalananDinasApi";
import LOGO_RKS from "../../../../assets/logo/LOGORKS.png";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const tglPanjang = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-");
  return y ? `${d} ${BULAN[Number(m) - 1]} ${y}` : "-";
};

// Kop dokumen formulir: judul + kotak No. Dokumen / Issued / Revised / Page (digambar di tiap halaman).
const KOP_SPPD = { docNo: "RKS-HRGA-FO-031", issued: "16 Agustus 2025", revised: "02" };
// Logo dimuat sekali sebagai data URL (jsPDF butuh data gambar, bukan URL aset).
let logoCache = null;
const muatLogo = async () => {
  if (logoCache) return logoCache;
  try {
    const blob = await (await fetch(LOGO_RKS)).blob();
    const url = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
    const dim = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight });
      im.onerror = rej;
      im.src = url;
    });
    logoCache = { url, ...dim };
  } catch {
    logoCache = { url: null };
  }
  return logoCache;
};

const gambarKopForm = (doc, judul, kop, halaman, total, logo) => {
  const x = 14, y = 10, w = 182, h = 22, bagi = 112;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(x, y, w, h);
  doc.line(x + bagi, y, x + bagi, y + h);
  const geser = logo && logo.url ? 24 : 0; // ruang logo di kiri atas kop
  if (geser) {
    const tinggi = h - 1;
    const lebarLogo = Math.min(geser - 1, (tinggi * logo.w) / logo.h);
    const tinggiLogo = (lebarLogo * logo.h) / logo.w;
    doc.addImage(logo.url, "PNG", x + 0.5, y + (h - tinggiLogo) / 2, lebarLogo, tinggiLogo);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const baris = judul.split("\n");
  baris.forEach((t, i) => doc.text(t, x + geser + (bagi - geser) / 2, y + h / 2 + (i - (baris.length - 1) / 2) * 6 + 1.5, { align: "center" }));
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  [["Doc. No", kop.docNo], ["Issued Date", kop.issued], ["Revised", kop.revised], ["Page", `${halaman} of ${total}`]].forEach(([k, v], i) => {
    const yy = y + 5 + i * 5;
    doc.text(k, x + bagi + 3, yy);
    doc.text(`:  ${v}`, x + bagi + 26, yy);
  });
};

const kotakTtd = (doc, kolom, y) => {
  const lebar = 182 / kolom.length;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  kolom.forEach((k, i) => {
    const cx = 14 + lebar * i + lebar / 2;
    doc.text(k.judul, cx, y, { align: "center" });
    k.orang.forEach((o, n) => {
      const yy = y + 24 + n * 12;
      doc.text(`(${o.nama})`, cx, yy, { align: "center" });
      if (o.peran) doc.text(o.peran, cx, yy + 4, { align: "center" });
    });
  });
};

// SURAT PENGAJUAN PERJALANAN DINAS (RKS-HRGA-FO-031) — tahap 1; Nomor SPPD terbit saat dikirim.
// Satu karyawan per SPPD; pembuat dokumen boleh orang lain. Penandatangan: Dibuat oleh (pembuat),
// Perwakilan/Pelaksana (personil yang ditugaskan), Diverifikasi oleh (approver kecuali terakhir), Disetujui oleh (terakhir).
export const cetakSuratPengajuan = async (d) => {
  const logo = await muatLogo();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = 38;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`No. SPPD : ${d.NomorSPPD || "(belum dikirim)"}`, doc.internal.pageSize.getWidth() / 2, y, { align: "center" });
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.text("Demi kepentingan perusahaan, kami mengajukan permohonan perjalanan dinas dengan rincian sebagai berikut:", 14, y);
  y += 3;
  autoTable(doc, {
    startY: y,
    body: [
      ["Kota Tujuan", ":", d.Tujuan, "Jumlah Hari", ":", `${d.JumlahHari} Hari`],
      ["Tanggal Berangkat", ":", tglPanjang(d.TanggalMulai), "Keperluan Dinas", ":", d.Keperluan || "-"],
      ["Tanggal Kembali", ":", tglPanjang(d.TanggalSelesai), "Jenis Dinas", ":", d.Kategori === "Penugasan" ? "Dinas Penugasan" : "Dinas Jabatan"],
    ],
    theme: "plain",
    styles: { fontSize: 10, cellPadding: 1 },
    columnStyles: { 0: { cellWidth: 36 }, 1: { cellWidth: 4 }, 2: { cellWidth: 48 }, 3: { cellWidth: 32 }, 4: { cellWidth: 4 }, 5: { cellWidth: "auto" } },
    margin: { left: 14, right: 14, top: 36 },
  });
  y = doc.lastAutoTable.finalY + 5;
  doc.text("Dengan Personil sebagai berikut :", 14, y);
  autoTable(doc, {
    startY: y + 2,
    head: [["No", "Nama", "NIK", "Jabatan", "Departemen", "Keterangan"]],
    body: [["1.", d.NamaKaryawan, d.Nik, d.NamaJabatan || "-", d.NamaBagian || "-", "Individu"]],
    styles: { fontSize: 9, lineColor: 0, lineWidth: 0.2, textColor: 0 },
    headStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold" },
    margin: { left: 14, right: 14, top: 36 },
  });
  y = doc.lastAutoTable.finalY + 5;
  doc.text("Dengan rincian Tugas Perjalanan Dinas :", 14, y);
  autoTable(doc, {
    startY: y + 2,
    head: [["Tanggal", "Waktu", "Kegiatan", "Lokasi", "Keterangan"]],
    body: (d.Rincian || []).map((r) => [
      String(r.Tanggal || "").split("-").reverse().join("-"),
      (r.Waktu || "-").replace("-", " – "),
      r.Kegiatan,
      r.Lokasi || "-",
      r.Keterangan || "",
    ]),
    styles: { fontSize: 9, lineColor: 0, lineWidth: 0.2, textColor: 0 },
    headStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold" },
    margin: { left: 14, right: 14, top: 36 },
  });
  y = doc.lastAutoTable.finalY + 6;

  if (y > 250) {
    doc.addPage();
    y = 40;
  }
  if (d.Keterangan && d.Keterangan.trim()) {
    doc.setFont("helvetica", "bold");
    doc.text("Catatan :", 14, y);
    doc.setFont("helvetica", "normal");
    y += 5;
    d.Keterangan.split("\n").filter((l) => l.trim()).forEach((l, i) => {
      const potong = doc.splitTextToSize(`${i + 1}.  ${l.trim()}`, 175);
      doc.text(potong, 14, y);
      y += potong.length * 4.5;
    });
    y += 4;
  }
  if (y > 215) {
    doc.addPage();
    y = 40;
  }
  doc.text(`Jakarta, ${tglPanjang(d.TanggalPengajuan)}`, 14, y);
  y += 6;

  const approver = (d.ApprovalList || []).map((a) => ({ nama: a.Nama, peran: a.Status === "✅" ? "(disetujui)" : "" }));
  // Perwakilan/Pelaksana = personil yang ditugaskan; Diverifikasi = semua approver kecuali terakhir; Disetujui = terakhir.
  const terakhir = approver.slice(-1);
  const verifikasi = approver.slice(0, -1);
  kotakTtd(
    doc,
    [
      { judul: "Dibuat oleh,", orang: [{ nama: d.DibuatOleh || "-", peran: d.DibuatOlehJabatan }] },
      { judul: "Perwakilan/Pelaksana", orang: [{ nama: d.NamaKaryawan, peran: d.NamaJabatan }] },
      { judul: "Diverifikasi oleh,", orang: verifikasi.length ? verifikasi : [{ nama: "-" }] },
      { judul: "Disetujui oleh,", orang: terakhir.length ? terakhir : [{ nama: "-" }] },
    ],
    y
  );

  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    gambarKopForm(doc, "SURAT PENGAJUAN\nPERJALANAN DINAS", KOP_SPPD, p, total, logo);
  }
  doc.save(`SuratPengajuan_${(d.NomorSPPD || d.PerjalananDinasID).replace(/\//g, "-")}.pdf`);
};

const KOP_BIAYA = { docNo: "RKS-HRGA-FO-031", issued: "16 Agustus 2025", revised: "02" };

// Kotak centang ☐/☑ digambar (font bawaan jsPDF tak punya glyph-nya).
const kotakCentang = (doc, x, y, centang) => {
  doc.setDrawColor(0);
  doc.setLineWidth(0.25);
  doc.rect(x, y - 3, 3.2, 3.2);
  if (centang) {
    doc.setLineWidth(0.5);
    doc.line(x + 0.6, y - 1.4, x + 1.4, y - 0.4);
    doc.line(x + 1.4, y - 0.4, x + 2.8, y - 2.6);
  }
  doc.setLineWidth(0.2);
};

const baris2 = (doc, y, kiri, kanan) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(kiri[0], 14, y);
  doc.text(`: ${kiri[1]}`, 48, y);
  if (kanan) {
    doc.text(kanan[0], 110, y);
    doc.text(`: ${kanan[1]}`, 142, y);
  }
};

// FORM BIAYA PERJALANAN DINAS (template Lampiran 3) — tahap 2: komponen biaya, moda, metode pembayaran.
export const cetakFormBiaya = async (d) => {
  const logo = await muatLogo();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setTextColor(0);
  let y = 42;
  baris2(doc, y, ["No. Surat Tugas", d.NomorSurat || "-"], ["Jumlah Hari", `${d.JumlahHari} Hari`]);
  baris2(doc, (y += 6), ["Kota Tujuan", d.Tujuan], ["Jumlah Personel", "1"]);
  baris2(doc, (y += 6), ["Tanggal Berangkat", tglPanjang(d.TanggalMulai)], ["Keperluan Dinas", (d.Keperluan || "-").slice(0, 40)]);
  baris2(doc, (y += 6), ["Tanggal Kembali", tglPanjang(d.TanggalSelesai)], ["Jenis Dinas", ""]);
  kotakCentang(doc, 144, y, d.Kategori === "Jabatan");
  doc.text("Dinas Jabatan", 148.5, y);
  kotakCentang(doc, 144, y + 5, d.Kategori === "Penugasan");
  doc.text("Dinas Penugasan", 148.5, y + 5);
  y += 12;

  doc.setFont("helvetica", "bold");
  doc.text("Rincian Personel", 14, y);
  autoTable(doc, {
    startY: y + 2,
    head: [["No", "Nama", "NIK", "Jabatan", "Departemen"]],
    body: [["1.", d.NamaKaryawan, d.Nik, d.NamaJabatan || "-", d.NamaBagian || "-"]],
    styles: { fontSize: 9, lineColor: 0, lineWidth: 0.2, textColor: 0 },
    headStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold" },
    margin: { left: 14, right: 14, top: 36 },
  });
  y = doc.lastAutoTable.finalY + 6;

  doc.setFont("helvetica", "bold");
  doc.text("Rincian:", 14, y);
  doc.setFont("helvetica", "normal");
  doc.text("Transportasi:", 14, y + 6);
  let x = 40;
  ["Pesawat", "Mobil", "Kapal", "Bus", "Kereta", "Lainnya"].forEach((m) => {
    kotakCentang(doc, x, y + 6, (d.ModaTransportasi || []).includes(m));
    const label = m === "Lainnya" ? `Lainnya ${d.ModaLainnya || "____________"}` : m;
    doc.text(label, x + 4.5, y + 6);
    x += doc.getTextWidth(label) + 10;
  });
  y += 10;

  // Tabel komponen: urutan template; "Uang Saku" dari tarif jabatan (Penugasan); nama lama ditambahkan di bawahnya.
  const saku = d.Kategori === "Penugasan" ? Number(d.EstimasiUangSaku || 0) : 0;
  const susunan = ["Tiket Perjalanan", "Transport PP Bandara", "Uang Makan", "Uang Saku", "Transport Lokal", "Hotel/Akomodasi", "Laundry", "Tol / Parkir", "Bagasi", "Lainnya"];
  const biaya = Array.isArray(d.Biaya) ? d.Biaya : [];
  const lain = biaya.filter((b) => !susunan.includes(b.Jenis));
  let jumlah = saku;
  const rows = susunan.map((k) => {
    if (k === "Uang Saku") return [k, saku ? d.JumlahHari : "", saku ? "hari" : "", saku ? rupiah(saku) : "", saku ? "Tarif jabatan x hari" : ""];
    const b = biaya.find((x) => x.Jenis === k);
    if (!b) return [k, "", "", "", ""];
    jumlah += Number(b.Nominal) || 0;
    return [k, b.Qty || "", b.Satuan || "", rupiah(b.Nominal), b.Keterangan || ""];
  });
  lain.forEach((b) => {
    jumlah += Number(b.Nominal) || 0;
    rows.push([b.Jenis, b.Qty || "", b.Satuan || "", rupiah(b.Nominal), b.Keterangan || ""]);
  });
  rows.push([{ content: "Jumlah", styles: { fontStyle: "bold" } }, "", "", { content: rupiah(jumlah), styles: { fontStyle: "bold" } }, ""]);
  autoTable(doc, {
    startY: y,
    head: [["Komponen Biaya", "Qty", "Satuan", "Total", "Keterangan"]],
    body: rows,
    styles: { fontSize: 9, lineColor: 0, lineWidth: 0.2, textColor: 0 },
    headStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold" },
    columnStyles: { 1: { halign: "right", cellWidth: 16 }, 2: { cellWidth: 22 }, 3: { halign: "right", cellWidth: 34 } },
    margin: { left: 14, right: 14, top: 36 },
  });
  y = doc.lastAutoTable.finalY + 6;

  if (y > 235) {
    doc.addPage();
    y = 42;
  }
  doc.setFont("helvetica", "bold");
  doc.text("Metode Pembayaran :", 14, y);
  doc.setFont("helvetica", "normal");
  x = 52;
  [["Transfer", "Transfer ke rekening karyawan"], ["Cash", "Cash"], ["CorporateCard", "Corporate Card"], ["Lainnya", "Lainnya"]].forEach(([v, l]) => {
    kotakCentang(doc, x, y, d.MetodePembayaran === v);
    const label = v === "Lainnya" && d.MetodeLainnya ? `Lainnya ${d.MetodeLainnya}` : l;
    doc.text(label, x + 4.5, y);
    x += doc.getTextWidth(label) + 9;
  });
  y += 10;
  doc.text(`Jakarta, ${d.TanggalBiaya ? tglPanjang(d.TanggalBiaya) : "__________________"}`, 14, y);
  y += 6;
  const apv = d.ApprovalList || [];
  const hr = apv[Math.max(0, apv.length - 2)] || {};
  kotakTtd(
    doc,
    [
      { judul: "Diverifikasi oleh,", orang: [{ nama: "_______________________", peran: "Finance" }] },
      { judul: "Diperiksa oleh,", orang: [{ nama: hr.Nama || "_______________________", peran: "Human Resource" }] },
    ],
    y
  );

  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    gambarKopForm(doc, "FORM BIAYA\nPERJALANAN DINAS", KOP_BIAYA, p, total, logo);
  }
  doc.save(`FormBiaya_${(d.NomorSPPD || d.PerjalananDinasID).replace(/\//g, "-")}.pdf`);
};

// FORM PERTANGGUNGJAWABAN PERJALANAN DINAS (template Lampiran 4) — tahap 4 (LPD): laporan pelaksanaan,
// realisasi biaya vs anggaran Form Biaya, dan perhitungan sisa/kekurangan dana. `l` = laporan (LPD).
export const cetakPertanggungjawaban = async (l) => {
  const logo = await muatLogo();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setTextColor(0);
  let y = 42;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`No. Surat Tugas : ${l.NomorSurat || "-"}`, 14, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.text("Personel pelaksana :", 14, y);
  const tabel = (head, body, extra = {}) => {
    autoTable(doc, {
      startY: y + 2,
      head: [head],
      body,
      styles: { fontSize: 9, lineColor: 0, lineWidth: 0.2, textColor: 0 },
      headStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold" },
      margin: { left: 14, right: 14, top: 36 },
      ...extra,
    });
    y = doc.lastAutoTable.finalY + 6;
  };
  tabel(["No", "Nama", "NIK", "Jabatan", "Departemen"], [["1.", l.NamaKaryawan, l.Nik, l.NamaJabatan || "-", l.NamaBagian || "-"]]);

  doc.text("Laporan Pelaksanaan Perjalanan Dinas :", 14, y);
  const kegiatan = Array.isArray(l.Kegiatan) ? l.Kegiatan : [];
  tabel(
    ["No", "Tanggal", "Lokasi", "Kegiatan/Tugas yang dilaksanakan", "Hasil/Output"],
    kegiatan.length
      ? kegiatan.map((k, i) => [`${i + 1}.`, String(k.Tanggal || "").split("-").reverse().join("-"), k.Lokasi || "-", k.Kegiatan, k.Hasil || ""])
      : [["1.", "", "", "", ""]],
    { columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 24 } } }
  );
  if (l.HasilPerjalanan && l.HasilPerjalanan.trim()) {
    const t = doc.splitTextToSize(`Ringkasan hasil: ${l.HasilPerjalanan.trim()}`, 182);
    doc.text(t, 14, y - 2);
    y += t.length * 4.5 + 2;
  }

  if (y > 200) {
    doc.addPage();
    y = 42;
  }
  doc.text("Rincian realisasi biaya :", 14, y);
  // Per komponen: Anggaran (Form Biaya) vs Realisasi (LPD); urutan template + komponen lain yang terpakai.
  const susunan = ["Tiket Perjalanan", "Transport Lokal", "Hotel/Akomodasi", "Laundry", "Tol / Parkir", "Bagasi", "Lainnya"];
  const anggaran = Array.isArray(l.AnggaranBiaya) ? l.AnggaranBiaya : [];
  const realisasi = Array.isArray(l.Biaya) ? l.Biaya : [];
  const lainnya = [...new Set([...anggaran, ...realisasi].map((b) => b.Jenis))].filter((j) => !susunan.includes(j));
  const jumlah = (arr, j) => arr.filter((b) => b.Jenis === j).reduce((a, b) => a + (Number(b.Nominal) || 0), 0);
  let tAng = 0;
  let tReal = 0;
  const rows = [];
  [...susunan, ...lainnya].forEach((j) => {
    const a = jumlah(anggaran, j);
    const r = jumlah(realisasi, j);
    const bukti = realisasi.filter((b) => b.Jenis === j && b.NoBukti).map((b) => b.NoBukti).join(", ");
    tAng += a;
    tReal += r;
    rows.push([`${rows.length + 1}.`, j, a ? rupiah(a) : "", r ? rupiah(r) : "", a || r ? rupiah(r - a) : "", bukti]);
  });
  if (Number(l.UangSaku) > 0) {
    tAng += Number(l.UangSaku);
    tReal += Number(l.UangSaku);
    rows.push([`${rows.length + 1}.`, "Uang Saku", rupiah(l.UangSaku), rupiah(l.UangSaku), rupiah(0), ""]);
  }
  rows.push(["", { content: "Total", styles: { fontStyle: "bold" } }, { content: rupiah(tAng), styles: { fontStyle: "bold" } }, { content: rupiah(tReal), styles: { fontStyle: "bold" } }, { content: rupiah(tReal - tAng), styles: { fontStyle: "bold" } }, ""]);
  tabel(["No", "Komponen", "Anggaran", "Realisasi", "Selisih", "No. Bukti/Kwitansi"], rows, {
    columnStyles: { 0: { cellWidth: 10 }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
  });

  if (y > 225) {
    doc.addPage();
    y = 42;
  }
  const diterima = Number(l.UangMukaDiterima) || 0;
  const selisih = Number(l.Selisih) || 0; // realisasi (+uang saku) − uang muka; >0 kurang bayar
  doc.setFont("helvetica", "normal");
  doc.text(`Total dana yang diterima/advance: ${rupiah(diterima)}`, 14, y);
  doc.text(`Total realisasi biaya: ${rupiah(Number(l.TotalRealisasi || 0) + Number(l.UangSaku || 0))}`, 14, y + 5.5);
  doc.text(`Sisa dana yang harus dikembalikan: ${rupiah(selisih < 0 ? -selisih : 0)}`, 14, y + 11);
  doc.text(`Kekurangan dana yang harus dibayarkan perusahaan: ${rupiah(selisih > 0 ? selisih : 0)}`, 14, y + 16.5);
  y += 26;
  doc.text(`Jakarta, ${l.TanggalLaporan ? tglPanjang(l.TanggalLaporan) : "__________________"}`, 14, y);
  y += 6;
  const apv = l.ApprovalList || [];
  // Alur Pertanggungjawaban: HR → Finance → Accounting (urutan approver).
  const hr = apv[0] || {};
  const fin = apv[1] || {};
  const acc = apv[2] || {};
  kotakTtd(
    doc,
    [
      { judul: "Dibuat oleh,", orang: [{ nama: l.NamaKaryawan, peran: "Pelaksana" }] },
      { judul: "Diverifikasi oleh,", orang: [{ nama: hr.Nama || "_______________", peran: "Human Resource" }] },
      { judul: "Diverifikasi oleh,", orang: [{ nama: acc.Nama || "_______________", peran: "Accounting" }] },
      { judul: "Diperiksa oleh,", orang: [{ nama: fin.Nama || "_______________", peran: "Finance" }] },
    ],
    y
  );

  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    gambarKopForm(doc, "FORM PERTANGGUNGJAWABAN\nPERJALANAN DINAS", KOP_BIAYA, p, total, logo);
  }
  doc.save(`Pertanggungjawaban_${(l.NomorSPPD || l.PerjalananDinasID).replace(/\//g, "-")}.pdf`);
};

const KOP_ST = { docNo: "RKS-HRGA-FO-031", issued: "21 Juni 2026", revised: "00" };

// SURAT TUGAS PERJALANAN DINAS (template Lampiran 2) — hanya kategori Penugasan, bernomor saat approval
// final (NN.NNN/RKSHO-HR/ST/X/YYYY). Dicetak untuk pengajuan Selesai (99).
export const cetakSuratTugas = async (d) => {
  const logo = await muatLogo();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const lebar = doc.internal.pageSize.getWidth();
  let y = 42;
  doc.setTextColor(0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("PT. RANJI KARYA SAKTI", lebar / 2, y, { align: "center" });
  y += 8;
  doc.setFontSize(10);
  doc.text(`Nomor Surat Tugas : ${d.NomorSurat || "-"}`, 14, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.text(`Berdasarkan No. SPD : ${d.NomorSPPD || "-"}`, 14, y);
  y += 6;
  doc.text("Dengan ini memberikan tugas kepada:", 14, y);
  autoTable(doc, {
    startY: y + 2,
    head: [["No", "Nama", "NIK", "Jabatan", "Departemen", "Keterangan"]],
    body: [["1.", d.NamaKaryawan, d.Nik, d.NamaJabatan || "-", d.NamaBagian || "-", "Individu"]],
    styles: { fontSize: 9, lineColor: 0, lineWidth: 0.2, textColor: 0 },
    headStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold" },
    margin: { left: 14, right: 14, top: 36 },
  });
  y = doc.lastAutoTable.finalY + 6;
  doc.setFontSize(10);
  doc.text(`Tujuan: ${d.Tujuan}, ${tglPanjang(d.TanggalMulai)} s/d ${tglPanjang(d.TanggalSelesai)} (${d.JumlahHari} hari)`, 14, y);
  y += 5;
  const keperluan = doc.splitTextToSize(`Keperluan: ${d.Keperluan || "-"}`, 182);
  doc.text(keperluan, 14, y);
  y += keperluan.length * 4.5 + 3;
  doc.text("Surat Tugas ini berlaku sejak tanggal ditetapkan sampai dengan selesainya pelaksanaan tugas dinas.", 14, y);
  y += 6;
  doc.text("Demikian Surat Tugas Dinas ini dibuat untuk dipergunakan sebagaimana mestinya.", 14, y);
  y += 12;

  // Penandatangan = HR (verifikator pertama di rantai approval); tanggal = approval terakhir.
  const apv = d.ApprovalList || [];
  const hr = apv[Math.max(0, apv.length - 2)] || {}; // rantai: Head Dept → HR → Direktur
  const [yy, mm, dd] = String(d.TanggalDisetujui || "").split("-");
  const x = 120;
  doc.text("Dikeluarkan di : Jakarta", x, y);
  doc.text(`Tanggal : ${yy ? `${dd} / ${mm} / ${yy}` : "____ / ____ / ______"}`, x, y + 5);
  doc.text("Mengeluarkan,", x, y + 14);
  doc.text(`( ${hr.Nama || "_____________________"} )`, x, y + 38);
  doc.text("Human Resource", x, y + 43);
  doc.setFont("helvetica", "bold");
  doc.text("PT. Ranji Karya Sakti", 14, y + 43);

  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    gambarKopForm(doc, "SURAT TUGAS\nPERJALANAN DINAS", KOP_ST, p, total, logo);
  }
  doc.save(`SuratTugas_${(d.NomorSurat || d.PerjalananDinasID).replace(/\//g, "-")}.pdf`);
};
